/* eslint-disable react-hooks/refs -- Intentional render-time ref access for recycling detection */
import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';

/** Resets resolved-state refs when @pierre/diffs reuses this component for a different thread. */
export function useResetResolvedStateOnThreadRecycled(params: {
  comments: PrV2CommentResponse[];
  thread: PrV2ReviewThread | undefined;
  previousResolvedThreadIdRef: MutableRefObject<string | null>;
  previousEffectiveResolvedRef: MutableRefObject<boolean>;
  serverResolved: boolean;
  setLocalResolvedOverride: (value: boolean | null) => void;
  setIsResolvedExpanded: (value: boolean) => void;
}): boolean {
  const {
    comments,
    thread,
    previousResolvedThreadIdRef,
    previousEffectiveResolvedRef,
    serverResolved,
    setLocalResolvedOverride,
    setIsResolvedExpanded
  } = params;
  const rootCommentId = comments[0]?.githubCommentId ?? null;
  const previousRootCommentIdRef = useRef(rootCommentId);
  const wasRecycled = previousRootCommentIdRef.current !== rootCommentId;
  if (wasRecycled) {
    previousRootCommentIdRef.current = rootCommentId;
    previousResolvedThreadIdRef.current = thread?.id ?? null;
    previousEffectiveResolvedRef.current = Boolean(thread?.isResolved);
  }
  useEffect(() => {
    if (wasRecycled) {
      setLocalResolvedOverride(null);
      setIsResolvedExpanded(!serverResolved);
    }
  }, [wasRecycled, serverResolved, setLocalResolvedOverride, setIsResolvedExpanded]);
  return wasRecycled;
}